<template>
    <section class="component" style="position: relative">
        <section class="component__header">
            <span class="component__header__title">
                {{$t('com.file.visualization-link-settings.title')}}
            </span>
            <!-- historia dotyczy konkretnego adresu, więc przycisk pojawia się dopiero, gdy adres istnieje -->
            <section class="component__header__action" v-if="hasLink">
                <button class="component__button component__button--small component__button--white"
                        @click="openActivity">
                    <img src="@/assets/icons/components/clock.svg" />
                    <span class="component__button__text">{{$t('com.file.visualization-link-activity.button')}}</span>
                </button>
            </section>
        </section>
        <section class="component__body" v-if="!isLoading">
            <section class="d-flex flex-column gap-3 px-3">
                <!-- gotowy adres - dopiero on uruchamia okno wizualizacji -->
                <section v-if="hasLink && !isEditing" class="d-flex flex-column gap-2">
                    <!-- adres w osobnej linii - obok trzech przycisków zostawał z niego skrawek -->
                    <input class="visualization-link" type="text" readonly :value="link" @focus="$event.target.select()" />
                    <section class="d-flex flex-row flex-wrap gap-2 align-items-center">
                        <ButtonOutline @click="copyLink" color="light">
                            {{$t('com.file.visualization-link-settings.copy')}}
                        </ButtonOutline>
                        <ButtonOutline @click="openLink" color="light">
                            {{$t('com.file.visualization-link-settings.open')}}
                        </ButtonOutline>
                        <ButtonOutline @click="startEditing" color="light">
                            {{$t('com.file.visualization-link-settings.change')}}
                        </ButtonOutline>
                    </section>
                    <p class="link-info">{{$t('com.file.visualization-link-settings.info')}}</p>
                </section>

                <!-- brak adresu albo zmiana istniejącego -->
                <section v-else class="d-flex flex-column gap-2">
                    <p class="link-info" v-if="!hasLink">{{$t('com.file.visualization-link-settings.not-created')}}</p>
                    <p class="link-warning" v-else>{{$t('com.file.visualization-link-settings.change-warning')}}</p>

                    <label class="address-label" for="visualization-slug">
                        {{$t('com.file.visualization-link-settings.address-label')}}
                    </label>
                    <!-- początek adresu w osobnej linii - w kolumnie 400 px nie mieści się obok pola -->
                    <span class="address-prefix">{{addressPrefix}}</span>
                    <input id="visualization-slug"
                           class="visualization-link"
                           type="text"
                           autocomplete="off"
                           spellcheck="false"
                           :maxlength="rules.maxLength"
                           v-model="slug"
                           @input="onSlugInput" />

                    <p class="link-status link-status--error" v-if="errorMessage">{{errorMessage}}</p>
                    <p class="link-status link-status--ok" v-else-if="isAvailable">{{$t('com.file.visualization-link-settings.available')}}</p>
                    <p class="link-info">{{$t('com.file.visualization-link-settings.rules', { min: rules.minLength, max: rules.maxLength })}}</p>

                    <!-- propozycja jako odnośnik, nie przycisk - razem z adresem nie zmieściłaby się w ramce -->
                    <button class="proposal"
                            v-if="proposal && proposal !== normalizedSlug"
                            @click="useProposal">
                        {{$t('com.file.visualization-link-settings.use-proposal', { proposal: proposal })}}
                    </button>

                    <section class="d-flex flex-row flex-wrap gap-2 align-items-center">
                        <ButtonBasic @click="save" color="danger" :disable="!canSave" :is-loading="isLoading">
                            {{hasLink ? $t('com.file.visualization-link-settings.save') : $t('com.file.visualization-link-settings.create')}}
                        </ButtonBasic>
                        <ButtonOutline v-if="hasLink" @click="cancelEditing" color="light">
                            {{$t('com.file.visualization-link-settings.cancel')}}
                        </ButtonOutline>
                    </section>
                </section>
            </section>
        </section>
        <SmallLoader v-if="isLoading" />

        <Transition>
            <VisualizationLinkActivityModal v-if="$store.state.modals.settings.visualizationLinkActivityModalStatus" :slug="savedSlug" />
        </Transition>
        <!-- eksport jako rodzeństwo historii, nie modal w modalu - oba i tak steruje sklep -->
        <Transition>
            <VisualizationLinkActivityExportModal v-if="$store.state.modals.settings.visualizationLinkActivityExportModalStatus" />
        </Transition>
    </section>
</template>
<script charset="utf-8">
    import SettingsService from "../../../../../services/settings.service";
    import ButtonOutline from "../../../../BasicControls/Buttons/ButtonOutline.vue";
    import ButtonBasic from "../../../../BasicControls/Buttons/ButtonBasic.vue";
    import SmallLoader from "../../../../BasicControls/SmallLoader.vue";
    import VisualizationLinkActivityModal from "./Modals/VisualizationLinkActivityModal.vue";
    import VisualizationLinkActivityExportModal from "./Modals/VisualizationLinkActivityExportModal.vue";
    import { copyToClipboard } from "../../../../../helpers/utils";
    import {
        VISUALIZATION_ROUTE_NAME,
        VISUALIZATION_SLUG_MAX_LENGTH,
        VISUALIZATION_SLUG_MIN_LENGTH,
        VISUALIZATION_SLUG_PATTERN,
        isVisualizationCodeWellFormed,
        normalizeVisualizationSlug,
        toTranslationKeySuffix
    } from "@/helpers/ow.route.helper";

    // Ile czekać po ostatnim znaku, zanim serwer sprawdzi zajętość adresu.
    const VALIDATION_DELAY_MS = 400;

    export default {
        name: "VisualizationLinkSettings",
        components: {
            ButtonOutline,
            ButtonBasic,
            SmallLoader,
            VisualizationLinkActivityModal,
            VisualizationLinkActivityExportModal
        },
        data() {
            return {
                isLoading: false,
                isEditing: false,
                hasLink: false,
                councilId: 0,
                savedSlug: "",
                slug: "",
                proposal: "",
                rules: {
                    minLength: VISUALIZATION_SLUG_MIN_LENGTH,
                    maxLength: VISUALIZATION_SLUG_MAX_LENGTH,
                    pattern: VISUALIZATION_SLUG_PATTERN
                },
                validationError: "",
                isAvailable: false,
                validationTimeout: null
            }
        },
        computed: {
            normalizedSlug() {
                return normalizeVisualizationSlug(this.slug);
            },
            /**
             * Jak w nakładce: ścieżkę daje router, origin przeglądarka (usługi za bramą nie znają publicznego
             * adresu), a język bieżący język panelu - ekran na sali nie ma jak go wybrać.
             */
            link() {
                return this.savedSlug ? this.buildLink(this.savedSlug) : "";
            },
            /**
             * Stały początek adresu: origin i identyfikator jednostki. Klient układa tylko to, co za nim.
             */
            addressPrefix() {
                const { href } = this.$router.resolve({
                    name: VISUALIZATION_ROUTE_NAME,
                    params: { councilId: this.councilId, code: "_" }
                });

                return `${window.location.origin}${href.replace(/_$/, "")}`;
            },
            errorMessage() {
                return this.validationError;
            },
            canSave() {
                return !this.isLoading
                    && this.normalizedSlug.length > 0
                    && this.normalizedSlug !== this.savedSlug
                    && !this.validationError;
            }
        },
        async mounted() {
            await this.getLink();
        },
        beforeUnmount() {
            this.clearValidationTimeout();
            this.$store.commit("setVisualizationLinkActivityModalStatus", false);
            this.$store.commit("setVisualizationLinkActivityExportModalStatus", false);
        },
        methods: {
            async getLink() {
                try {
                    this.isLoading = true;
                    const response = await SettingsService.getVisualizationLink();
                    const data = response.status === 200 ? response.data : null;

                    if (data === null || data === undefined) {
                        return;
                    }

                    this.applySettings(data);
                }
                catch {
                    this.hasLink = false;
                }
                finally {
                    this.isLoading = false;
                }
            },
            applySettings(data) {
                this.hasLink = data.hasLink === true;
                this.councilId = data.councilId || 0;
                this.savedSlug = data.slug || "";
                this.proposal = data.proposal || "";
                this.rules = {
                    minLength: data.minLength || VISUALIZATION_SLUG_MIN_LENGTH,
                    maxLength: data.maxLength || VISUALIZATION_SLUG_MAX_LENGTH,
                    pattern: data.pattern || VISUALIZATION_SLUG_PATTERN
                };
                this.isEditing = false;
                this.slug = this.hasLink ? this.savedSlug : this.proposal;
                this.validationError = "";
                this.isAvailable = false;

                // Wstawiona propozycja też przechodzi sprawdzenie - między odczytem a zapisem mógł ją ktoś zająć.
                if (!this.hasLink && this.slug) {
                    this.scheduleValidation();
                }
            },
            buildLink(slug) {
                const { href } = this.$router.resolve({
                    name: VISUALIZATION_ROUTE_NAME,
                    params: { councilId: this.councilId, code: slug },
                    query: { lang: this.$i18n.locale }
                });

                return `${window.location.origin}${href}`;
            },
            openActivity() {
                this.$store.commit("setVisualizationLinkActivityModalStatus", true);
            },
            onSlugInput() {
                // Reguły sprawdza najpierw przeglądarka - serwer pytany dopiero o adres, który je spełnia.
                this.isAvailable = false;
                this.validationError = "";
                this.scheduleValidation();
            },
            scheduleValidation() {
                this.clearValidationTimeout();
                this.validationTimeout = setTimeout(() => this.validate(), VALIDATION_DELAY_MS);
            },
            clearValidationTimeout() {
                if (this.validationTimeout !== null) {
                    clearTimeout(this.validationTimeout);
                    this.validationTimeout = null;
                }
            },
            async validate() {
                const candidate = this.normalizedSlug;

                if (candidate.length === 0 || candidate === this.savedSlug) {
                    this.validationError = "";
                    this.isAvailable = false;
                    return;
                }

                if (!isVisualizationCodeWellFormed(candidate, this.rules.pattern)) {
                    // Serwer i tak powtórzy walidację; tu chodzi o natychmiastową podpowiedź bez żądania.
                    this.applyValidationResult({ isValid: false, error: this.guessLocalError(candidate) }, candidate);
                    return;
                }

                try {
                    const response = await SettingsService.validateVisualizationLink(candidate);
                    this.applyValidationResult(response.data, candidate);
                }
                catch {
                    this.applyValidationResult({ isValid: false, error: "Unknown" }, candidate);
                }
            },
            /**
             * Wynik dotyczy konkretnego adresu - gdy pole zdążyło się zmienić, odpowiedź jest już nieaktualna.
             */
            applyValidationResult(result, candidate) {
                if (candidate !== this.normalizedSlug) {
                    return;
                }

                const isValid = result?.isValid === true;
                this.isAvailable = isValid;
                this.validationError = isValid ? "" : this.translateError(result?.error);
            },
            /**
             * Powód odrzucenia po stronie przeglądarki - te same nazwy, których używa serwer,
             * żeby komunikat pochodził z jednego zestawu tłumaczeń.
             */
            guessLocalError(candidate) {
                if (candidate.length === 0) {
                    return "Empty";
                }

                if (candidate.length < this.rules.minLength) {
                    return "TooShort";
                }

                if (candidate.length > this.rules.maxLength) {
                    return "TooLong";
                }

                if (!/^[a-z0-9-]+$/.test(candidate)) {
                    return "InvalidCharacters";
                }

                if (!/^[a-z]/.test(candidate)) {
                    return "MustStartWithLetter";
                }

                if (candidate.endsWith("-")) {
                    return "MustEndWithLetterOrDigit";
                }

                if (candidate.includes("--")) {
                    return "DoubleHyphen";
                }

                return "Unknown";
            },
            translateError(error) {
                const suffix = toTranslationKeySuffix(error);
                const key = `com.file.visualization-link-settings.error.${suffix || "unknown"}`;
                const message = this.$t(key, { min: this.rules.minLength, max: this.rules.maxLength });

                // Nieznana nazwa błędu nie może zostawić użytkownika z samym kluczem na ekranie.
                return message === key
                    ? this.$t("com.file.visualization-link-settings.error.unknown")
                    : message;
            },
            useProposal() {
                this.slug = this.proposal;
                this.onSlugInput();
            },
            startEditing() {
                this.isEditing = true;
                this.slug = this.savedSlug;
                this.validationError = "";
                this.isAvailable = false;
            },
            cancelEditing() {
                this.clearValidationTimeout();
                this.isEditing = false;
                this.slug = this.savedSlug;
                this.validationError = "";
                this.isAvailable = false;
            },
            async save() {
                const candidate = this.normalizedSlug;

                this.clearValidationTimeout();

                try {
                    this.isLoading = true;
                    const response = await SettingsService.saveVisualizationLink(candidate);

                    if (response.data?.isValid !== true) {
                        this.applyValidationResult(response.data, candidate);
                        return;
                    }

                    this.hasLink = true;
                    this.savedSlug = response.data.slug;
                    this.slug = response.data.slug;
                    this.isEditing = false;
                    this.isAvailable = false;
                    this.validationError = "";
                    this.$store.commit("showSuccessCustomMessage", this.$t('com.file.visualization-link-settings.saved'));
                }
                catch (error) {
                    // 422 to odrzucony adres z powodem w treści - inne błędy pokazuje już interceptor axiosa.
                    if (error?.response?.status === 422) {
                        this.applyValidationResult(error.response.data, candidate);
                        return;
                    }

                    this.validationError = this.$t("com.file.visualization-link-settings.error.unknown");
                }
                finally {
                    this.isLoading = false;
                }
            },
            async copyLink() {
                try {
                    await copyToClipboard(this.link);
                    this.$store.commit("showSuccessCustomMessage", this.$t('com.file.visualization-link-settings.link-copied'));
                }
                catch {
                    this.$store.commit("showError");
                }
            },
            openLink() {
                window.open(this.link, "_blank", "noopener");
            }
        }
    }
</script>
<style lang="scss" scoped>
    @use "@/assets/scss/user-profile-component.scss";

    .component {
        width: 400px;
        height: fit-content;
    }

    .visualization-link {
        width: 100%;
        min-width: 0;
        padding: 8px 10px;
        border: 1px solid #dce4ee;
        border-radius: 4px;
        background: #f7f9fc;
        font-size: 13px;
        color: #00082c;
    }

    .address-label {
        font-size: 12px;
        font-weight: 600;
        color: #00082c;
        margin: 0px;
    }

    .address-prefix {
        font-size: 11px;
        color: #666c7a;
        word-break: break-all;
        line-height: 130%;
    }

    /*
     * Prefiks w nazwach klas jest konieczny: IntegrationImportantDataSettings - kafelek obok na tej samej
     * stronie ustawień - ma niescopowany blok stylów z regułą
     * .info { text-align: center; max-width: 420px; margin-top: 30px }, która wyciekała na te akapity.
     */
    .link-info {
        font-size: 12px;
        color: #666c7a;
        margin: 0px;
        text-align: left;
    }

    .link-warning {
        font-size: 12px;
        color: #b26a00;
        margin: 0px;
        text-align: left;
    }

    .proposal {
        align-self: flex-start;
        max-width: 100%;
        padding: 0px;
        border: 0px;
        background: none;
        font-size: 12px;
        color: #004dcc;
        text-align: left;
        text-decoration: underline;
        word-break: break-all;

        &:hover {
            color: #003a99;
        }
    }

    .link-status {
        font-size: 12px;
        margin: 0px;
        text-align: left;

        &--error {
            color: #d0342c;
        }

        &--ok {
            color: #1b7f4f;
        }
    }
</style>
